import { ChangeEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker, TimeField, DateValidationError } from "@mui/x-date-pickers";
import { DateTime } from 'luxon';
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TextField } from "@mui/material";
import { AddressModel, BusinessAreaModel, ClientModel, ClientAkaModel, ClientStatus, EmailModel, ErpProductModel, PhoneModel, BillingModel, UpsertClientInput, NotificationFormatterModel, MissingAcknowledgementNotificationMode, ErpDocumentFormat, RequestResult } from "../../gql-types.generated";

import { getPrimaryAddressFromArray, getPrimaryEmailFromArray, getPrimaryPhoneFromArray } from '../../util/PrimaryFromModelArray';
import { ModelInputsSubmission, defaultCurrency, emptyGuid } from '../../util/Constants';
import { convertAddressModelToUpsertAddressInput, convertEmailModelToUpsertEmailInput, convertPhoneModelToUpsertPhoneInput } from '../../util/Common';
import { DialogGridColumn, DialogGridWithTopMargin, BlankMenuItem } from "../../util/SharedStyles";
import AddEditDialog from "./AddEditDialog";
import DialogClientAkaList from "../lists/DialogClientAkaList";
import IsaQualifierField from "../fields/IsaQualifierField";
import LocaleField from "../fields/LocaleField";
import AddressFields from "../fields/AddressFields";
import PhoneFields from "../fields/PhoneFields";
import { isEmailValid, isGuidValid, isURLValid } from "../../util/Validation";
import { capitalizeWithUnderscoreRemoval } from '../../util/Common';

import { fetchBillingList } from "../../features/Billing/BillingActions";
import { selectBillingList, selectError as billingSelectError, clearError } from "../../features/Billing/BillingSlice";
import { fetchErrorFormatterList } from "../../features/NotificationFormatters/NotificationFormattersActions";
import { selectErrorFormatterList } from "../../features/NotificationFormatters/NotificationFormattersSlice";
import { selectBusinessAreaList, selectErpProductList } from '../../features/EDIContainer/EDIContainerSlice';

interface ClientDialogProps {
    isOpen: boolean;
    clientInfo?: ClientModel;
    onClose: () => void;
    onSave: (
        clientInput: UpsertClientInput,
        keepDialogOpen?: boolean
    ) => void;
    refreshClientData?: () => void;
    error?: Error | undefined;
    clientSaveStatusResult?: RequestResult | undefined;
}


const ClientDialog: React.FC<ClientDialogProps> = props => {
    const { isOpen, clientInfo, onClose, onSave, refreshClientData, error, clientSaveStatusResult } = props;
    const dispatch = useAppDispatch();
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [clientId, setClientId] = useState<string | undefined>(undefined);
    // Form Fields
    const [address, setAddress] = useState<AddressModel | undefined>(undefined);
    const [businessAreaId, setBuisnessAreaId] = useState<string | undefined>(undefined);
    const [clientQ, setClientQ] = useState('');
    const [code, setCode] = useState('');
    const [email, setEmail] = useState<EmailModel | undefined>(undefined);
    const [erpProductId, setErpProductId] = useState<string | undefined>(undefined);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState<PhoneModel | undefined>(undefined);
    const [clientAKAs, setClientAKAs] = useState<ClientAkaModel[] | undefined>(undefined);
    const [status, setStatus] = useState<ClientStatus | undefined>(undefined);
    const [tenantId, setTenantId] = useState('');
    const [currencyCode, setCurrencyCode] = useState(defaultCurrency);
    const [billingId, setBillingId] = useState<string | undefined>(undefined);
    const [billingStartDate, setBillingStartDate] = useState<DateTime | undefined>(undefined);
    const [billingStartDateErrorText, setBillingStartDateErrorText] = useState('');
    const [billingStartDateInvalid, setBillingStartDateInvalid] = useState(false);
    const [errorFormatterId, setErrorFormatterId] = useState<string | undefined>(undefined);
    const [website, setWebsite] = useState('');
    const [emailInvalid, setEmailInvalid] = useState(false);
    const [emailErrorText, setEmailErrorText] = useState('');
    const [internalClientCode, setInternalClientCode] = useState('');
    const [localeCode, setLocaleCode] = useState<string | undefined>(undefined);
    const [tenantIdInvalid, setTenantIdInvalid] = useState(false);
    const [tenantIdErrorText, setTenantIdErrorText] = useState('');
    const [websiteErrorText, setWebsiteErrorText] = useState('');
    const [websiteInvalid, setWebsiteInvalid] = useState(false);
    const [notificationTime, setNotificationTime] = useState<DateTime | undefined>(undefined);
    const [notificationMode, setNotificationMode] = useState<MissingAcknowledgementNotificationMode | undefined>(undefined);
    const [erpDocumentFormat, setErpDocumentFormat] = useState<ErpDocumentFormat | undefined>(undefined);
    const [salesforceCountryCode, setSalesforceCountryCode] = useState('');
    const [salesforceCustomerNumber, setSalesforceCustomerNumber] = useState('');
    const [salesforceEntityCode, setSalesforceEntityCode] = useState('');

    const businessAreas = useAppSelector(selectBusinessAreaList);
    const erpProducts = useAppSelector(selectErpProductList);
    const errorFormatters = useAppSelector(selectErrorFormatterList);
    const billings = useAppSelector(selectBillingList);
    const fetchError = useAppSelector(billingSelectError);
     
    const notificationModes = Object.values(MissingAcknowledgementNotificationMode);
    const erpDocumentFormats = Object.values(ErpDocumentFormat);

    const currentDate = DateTime.now();
    // make the minimum date to be selected be no more than 3 months prior to current date
    // since really should be current and future, but allowing for if forget to setup in time
    const minBillingDate = currentDate.minus({ months: 3 });
    const [minValidBillingDate, setMinValidBillingDate] = useState<DateTime>(minBillingDate);
    
    useEffect(() => {
        dispatch(fetchBillingList());
        dispatch(fetchErrorFormatterList());
    }, []);

    useEffect(() => {
        if (clientInfo) {
            setFromClientInfo();
        } else {
            setToDefaults();
        }
    }, [clientInfo]);

    useEffect(() => {
        if (!isOpen) {
            setToDefaults();
            setSubmitted(false);
            setIsFormDirty(false);
            dispatch(clearError());
        } else {
            setFromClientInfo();
        }
    }, [isOpen]);

    useEffect(() => {
        // since the dialog can remain open with a preSave, set the submitted and dirty states to false
        // once the save status is received to stop the progress spinner from displaying
        if (clientSaveStatusResult) {
            setSubmitted(false);
            setIsFormDirty(false);
        }
    }, [clientSaveStatusResult]);

    const setFromClientInfo = () => {
        if (clientInfo) {
            let parentId = clientInfo.id;
            if (parentId) {
                setClientId(parentId);
            }
            setPhoneOrDefault(parentId);
            setEmailOrDefault(parentId);
            setAddressOrDefault(parentId);

            if (clientInfo.businessArea) {
                setBuisnessAreaId(clientInfo.businessArea.id);
            }
            if (clientInfo.isaQualifierCode) {
                setClientQ(clientInfo.isaQualifierCode);
            }
            if (clientInfo.clientCode) {
                setCode(clientInfo.clientCode);
            }
            if (clientInfo.erpProduct) {
                setErpProductId(clientInfo.erpProduct.id);
            }
            if (clientInfo.name) {
                setName(clientInfo.name);
            }
            if (clientInfo.statusCode) {
                setStatus(clientInfo.statusCode);
            }
            if (clientInfo.defaultCurrencyCode) {
                setCurrencyCode(clientInfo.defaultCurrencyCode);
            }
            if (clientInfo.billingId) {
                setBillingId(clientInfo.billingId);
            }
            if (clientInfo.billingStartDate) {
                // when billing date already has a value, we don't want it
                // to show as invalid since it could be in the past and
                // was valid at the time of entry, so set the min date to 
                // be the billingStartDate value to prevent error
                let startDate = DateTime.fromISO(clientInfo.billingStartDate);
                setMinValidBillingDate(startDate)
                setBillingStartDate(startDate);
            }
            if (clientInfo.internalClientCode) {
                setInternalClientCode(clientInfo.internalClientCode);
            }
            if (clientInfo.tenantId) {
                validateAndSetTenantId(clientInfo.tenantId);
            }
            if (clientInfo.website) {
                validateAndSetURL(clientInfo.website);
            }
            if (clientInfo.clientAKAs) {
                setClientAKAs(clientInfo.clientAKAs as ClientAkaModel[]);
            }
            if (clientInfo.errorFormatter) {
                setErrorFormatterId(clientInfo.errorFormatter.id);
            }
            if (clientInfo.bulkNotificationTime) {
                setNotificationTime(DateTime.fromISO(clientInfo.bulkNotificationTime))
            }
            if (clientInfo.missingAcknowledgementNotificationMode) {
                setNotificationMode(clientInfo.missingAcknowledgementNotificationMode);
            }
            if (clientInfo.erpDocumentFormat) {
                setErpDocumentFormat(clientInfo.erpDocumentFormat);
            }
            if (clientInfo.salesForceCountryCode) {
                setSalesforceCountryCode(clientInfo.salesForceCountryCode);
            }
            if (clientInfo.salesForceCustomerNumber) {
                setSalesforceCustomerNumber(clientInfo.salesForceCustomerNumber);
            }
            if (clientInfo.salesForceEntityCode) {
                setSalesforceEntityCode(clientInfo.salesForceEntityCode);
            }

            setLocaleCode(clientInfo?.localeCode as string);
           
        }
    };

    const setToDefaults = () => {
        setClientId(undefined);
        setBuisnessAreaId(undefined);
        setClientQ('');
        setCode('');
        setAddressDefaults(undefined);
        setEmailDefaults(undefined);
        setPhoneDefaults(undefined);
        setErpProductId(undefined);
        setName('');
        setStatus(ClientStatus.Active);
        setCurrencyCode(defaultCurrency);
        setTenantId('');
        setWebsite('');
        setBillingId(undefined);
        setBillingStartDate(undefined);
        setBillingStartDateErrorText('');
        setBillingStartDateInvalid(false);
        setClientAKAs(undefined);
        setInternalClientCode('');
        setNotificationTime(undefined);
        setErrorFormatterId(undefined);
        setMinValidBillingDate(minBillingDate);
        setNotificationMode(undefined);
        setLocaleCode(undefined);
        setErpDocumentFormat(undefined);
        setSalesforceCountryCode('');
        setSalesforceCustomerNumber('');
        setSalesforceEntityCode('');
    };

    const setAddressDefaults = (parentId: string | undefined) => {
        setAddress({
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            addressLine4: '',
            city: '',
            countryCode: '',
            postalCode: '',
            stateId: '',
            isPrimary: true,
            parentId: parentId,
        } as AddressModel);
    }

    const setEmailDefaults = (parentId: string | undefined) => {
        setEmail({
            email: '',
            parentId: parentId,
            isPrimary: true,
        } as EmailModel);
    }

    const setPhoneDefaults = (parentId: string | undefined) => {
        setPhone({
            phoneNumber: '',
            description: '',
            parentId: parentId,
            isPrimary: true,
        } as PhoneModel);
    }

    const setAddressOrDefault = (parentId: string | undefined) => {
        if (clientInfo?.addresses) {
            const primary = getPrimaryAddressFromArray(clientInfo.addresses as AddressModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    setAddress(newPrimary);
                } else {
                    setAddress(primary);
                }
            } else {
                setAddressDefaults(parentId);
            }
        } else {
            setAddressDefaults(parentId);
        }
    }

    const setPhoneOrDefault = (parentId: string | undefined) => {
        if (clientInfo?.phones) {
            const primary = getPrimaryPhoneFromArray(clientInfo.phones as PhoneModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    setPhone(newPrimary);
                } else {
                    setPhone(primary);
                }
            } else {
                setPhoneDefaults(parentId);
            }
        } else {
            setPhoneDefaults(parentId);
        }
    }

    const setEmailOrDefault = (parentId: string | undefined) => {
        if (clientInfo?.emails) {
            const primary = getPrimaryEmailFromArray(clientInfo.emails as EmailModel[]);
            if (primary) {
                if (primary.isPrimary === undefined) {
                    // primary is not extensible, since it wasn't returned with this property, we can not add it
                    // make a clone and set the property before setting to the state
                    const newPrimary = Object.assign({}, primary, { isPrimary: true, parentId: parentId });
                    validateAndSetEmail(newPrimary.email as string, newPrimary);
                } else {
                    validateAndSetEmail(primary.email as string, primary);
                }
            } else {
                setEmailDefaults(parentId);
            }
        } else {
            setEmailDefaults(parentId);
        }
    }

    const isFormValid = () => {
        let requiredCheck = (
            isFormDirty && 
            clientQ?.trim().length > 0 && 
            code?.trim().length > 0 && 
            name?.trim().length > 0 && 
            currencyCode?.trim().length > 0 &&
            erpProductId !== undefined && erpProductId?.trim().length > 0 &&
            billingId !== undefined && billingId?.trim().length > 0
        );

        if (requiredCheck) {
            let validEmail = true;
            let validWebsite = true;
            let validBilling = !billingStartDateInvalid;
            let tenantIdValid = !tenantIdInvalid;
            if (email && email.email) {
                validEmail = !emailInvalid;
            } else {
                requiredCheck = false;
            }

            return requiredCheck && validEmail && validWebsite && tenantIdValid && validBilling;
        }
        return false;
    };

    const checkAddressModelForData = () => {
        if (address?.addressLine1 !== '' ||
            address?.addressLine1 !== '' ||
            address?.addressLine2 !== '' ||
            address?.addressLine3 !== '' ||
            address?.addressLine4 !== '' ||
            address?.city !== '' ||
            address?.countryCode !== '' ||
            address?.postalCode !== '' ||
            address?.stateId !== '') {
            return address;
        } else {
            return undefined;
        }
    }

    const getModelInputsData = () => {
        const emailVal = email?.email as string;
        const phoneVal = phone?.phoneNumber as string;
        let ret = { email: undefined, phone: undefined, address: checkAddressModelForData() } as ModelInputsSubmission;
        if (emailVal !== '') {
            ret.email = email;
        }
        if (phoneVal !== '') {
            ret.phone = phone;
        }
        return ret;
    }

    const onError = () => {
        setSubmitted(false);
    }

    const submitForm = (isPresave?: boolean) => {
        if (isFormValid()) {
            const models = getModelInputsData();
            setSubmitted(true);
            let addressInput = convertAddressModelToUpsertAddressInput(models.address);
            const addresses = addressInput ? [addressInput] : undefined;
            let emailInput = convertEmailModelToUpsertEmailInput(models.email);
            const emails = emailInput ? [emailInput] : undefined;
            let phoneInput = convertPhoneModelToUpsertPhoneInput(models.phone);
            const phones = phoneInput ? [phoneInput] : undefined;
            onSave(
                { 
                    tenantId: tenantId,
                    addresses: addresses,
                    businessAreaId: businessAreaId,
                    isaQualifierCode: clientQ,
                    clientCode: code,
                    emails: emails,
                    erpProductId: erpProductId,
                    id: clientId,
                    internalClientCode: internalClientCode,
                    name: name,
                    phones: phones,
                    statusCode: status,
                    website: website,
                    billingId: billingId,
                    billingStartDate: billingStartDate,
                    bulkNotificationTime: notificationTime,
                    errorFormatterId: errorFormatterId,
                    missingAcknowledgementNotificationMode: notificationMode,
                    localeCode: localeCode,
                    defaultCurrencyCode: currencyCode ?? defaultCurrency,
                    erpDocumentFormat: erpDocumentFormat,
                    salesForceCountryCode: salesforceCountryCode,
                    salesForceCustomerNumber: salesforceCustomerNumber,
                    salesForceEntityCode: salesforceEntityCode
                },
                isPresave
             );
        }
    };

    const preSaveClientData = () => {
      // submit form with isPresave = true to save the data without closing the dialog
      submitForm(true);  
    };

    const validateAndSetTenantId = (toValidate: string) => {
        let isValid = true;
        if (toValidate && toValidate.length > 0) {
            isValid = isGuidValid(toValidate, true);
        }
        setTenantIdInvalid(!isValid);
        if (isValid) {
            // only set dirty if value is different than initial
            if (toValidate !== clientInfo?.tenantId) {
                setIsFormDirty(true);
            }
            setTenantIdErrorText('');
        } else {
            setTenantIdErrorText('Invalid format.  Enter a valid GUID.');
        }
        setTenantId(toValidate);
    };

    const validateAndSetEmail = (toValidate: string, clientEmail?: EmailModel) => {
        if (toValidate || toValidate === "") {
            const isValid = isEmailValid(toValidate);
            setEmailInvalid(!isValid);
            if (isValid) {
                // if clientEmail exists, we are in initial set, so don't want the form dirty
                if (clientEmail === undefined) {
                    setIsFormDirty(true);
                }
                setEmailErrorText('');
            } else {
                setEmailErrorText('Invalid format.  Enter a valid email address');
            }
            // assign the passed in email (initial set only), or the stored email
            let currEmail = clientEmail || email;
            if (currEmail) {
                const justMail = { email: toValidate };
                let newMail = {};
                Object.assign(newMail, currEmail, justMail);
                setEmail(newMail as EmailModel);
            } else {
                setEmail({
                    email: toValidate,
                    isPrimary: true,
                    parentId: clientId,
                } as EmailModel);
            }
        }
    };
    const validateAndSetURL = (toValidate: string) => {
        if (toValidate || toValidate === "") {
            const isValid = isURLValid(toValidate);
            setWebsiteInvalid(!isValid);
            if (isValid) {
                // only set dirty if value is different than initial
                if (toValidate !== clientInfo?.website) {
                    setIsFormDirty(true);
                }
                setWebsiteErrorText('');
            } else {
                setWebsiteErrorText('Invalid format.  Enter a valid URL.');
            }
            setWebsite(toValidate);
        }
    }

    const onAddressChange = (address: AddressModel) => {
        setIsFormDirty(true);
        setAddress(address);
    };
    const onBusinessAreaChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setBuisnessAreaId(event.target.value);
        } else {
            setBuisnessAreaId(undefined);
        }
    };
    const onClientQChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        setClientQ(event.target.value ?? '');
    };
    const onCurrencyCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        if (event.target.value && event.target.value.length > 0) {
            setCurrencyCode(event.target.value.toUpperCase());
        } else {
            setCurrencyCode('');
        }
    };
    const onLocaleChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value && event.target.value.length > 0) {
            setLocaleCode(event.target.value);
        } else {
            setLocaleCode(undefined);
        }
    };
    const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setCode(event.target.value);
    };
    const onTenantIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetTenantId(event.target.value);
    };
    const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetEmail(event.target.value);
    };
    const onWebsiteChange = (event: ChangeEvent<HTMLInputElement>) => {
        validateAndSetURL(event.target.value);
    };
    const onErpProductChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setErpProductId(event.target.value);
        } else {
            setErpProductId(undefined);
        }
    };
    const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setName(event.target.value);
    };
    const onInternalClientCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setInternalClientCode(event.target.value);
    };
    const onPhoneNumberChange = (phoneModelObj: PhoneModel) => {
        setIsFormDirty(true);
        setPhone(phoneModelObj);
    };
    const onStatusChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        const key = event.target.value;
        if (key) {
            setStatus(key as ClientStatus);
        } else {
            setStatus(undefined);
        }
    };
    const onNotificationTimeChange = (value: DateTime | null) => {
        setIsFormDirty(true);
        setNotificationTime(value ?? undefined);
    };
    const onNotificationModeChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        const key = event.target.value;
        if (key) {
            setNotificationMode(key as MissingAcknowledgementNotificationMode);
        } else {
            setNotificationMode(undefined);
        }
    };
    const onErpDocumentFormatChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        const key = event.target.value;
        if (key) {
            setErpDocumentFormat(key as ErpDocumentFormat);
        } else {
            setErpDocumentFormat(undefined);
        }
    };
    const onErrorFormatterChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setErrorFormatterId(event.target.value);
        } else {
            setErrorFormatterId(undefined);
        }
    };
    const onSfCustomerNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setSalesforceCustomerNumber(event.target.value);
    };
    const onSfCountryCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setSalesforceCountryCode(event.target.value);
    };
    const onSfEntityCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIsFormDirty(true);
        setSalesforceEntityCode(event.target.value);
    };
    const onBillingGroupChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setBillingId(event.target.value);
        } else {
            setBillingId(undefined);
        }
    };
    const onBillingStartDateChange = (value: DateTime | null) => {
        // once the date has changed, we no longer care if a previous date was set,
        // so set the 3 month min date for validation if it isn't set to that yet
        if (minValidBillingDate !== minBillingDate) {
            setMinValidBillingDate(minBillingDate);
        } 
        setIsFormDirty(true);
        setBillingStartDate(value ?? undefined);
    };

    const onBillingStartDateError = (reason: DateValidationError, value: DateTime | null) => {
        let errorReason = reason;
        if (errorReason) {
            setBillingStartDateInvalid(true);
            if (errorReason === "minDate") {
                setBillingStartDateErrorText("Date must be within last 3 months or future.")
            } else {
                setBillingStartDateErrorText("Invalid Date");
            }
        } else {
            // null errorReason means valid
            setBillingStartDateInvalid(false);
            setBillingStartDateErrorText("");
        }
    };

    const getErpProductsOptions = () => {
        if (erpProducts && erpProducts.length) {
            return (
                erpProducts.map((product: ErpProductModel) => (
                    <MenuItem
                        key={product.id}
                        value={product.id}
                    >
                        {product.name}
                    </MenuItem>
                ))
            );
        }
        return null;
    };

    const getBuisnessAreaOptions = () => {
        if (businessAreas && businessAreas.length) {
            return (
                businessAreas.map((businessArea: BusinessAreaModel) => (
                    <MenuItem
                        key={businessArea.id}
                        value={businessArea.id}
                    >
                        {businessArea.name}
                    </MenuItem>
                ))
            );
        }
        return null;
    };

    const getErrorFormatterOptions = () => {
        if (errorFormatters && errorFormatters.length) {
            let items = [];
            // using emptyGuid value to pass to backend as way to indicate to save a null value
            const blankItem = <BlankMenuItem key="0" value={emptyGuid}></BlankMenuItem>;
            const dynamicItems = errorFormatters.map((formatter: NotificationFormatterModel) => (
                <MenuItem
                    key={formatter.id}
                    value={formatter.id}
                >
                {formatter.description}
                </MenuItem>
            ));
            items.push(blankItem);
            items.push(...dynamicItems);
            return items;
        }
        return null;
    };

    const getBillingOptions = () => {
        if (billings && billings.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value={''}></BlankMenuItem>;
            const dynamicItems = billings.map((billing: BillingModel) => (
                <MenuItem
                    key={billing.id}
                    value={billing.id}
                >
                    {`${billing.description} (${billing.currencyCode})`}
                </MenuItem>
            ));
            items.push(blankItem);
            items.push(...dynamicItems);
            return items;
        }
        return null;
    };

    const getStatusOptions = () => {
        return [
            <MenuItem key={ClientStatus.Active} value={ClientStatus.Active}>
                Active
            </MenuItem>,
            <MenuItem key={ClientStatus.Inactive} value={ClientStatus.Inactive}>
                Inactive
            </MenuItem>,
            <MenuItem key={ClientStatus.Inprocess} value={ClientStatus.Inprocess}>
                In-process
            </MenuItem>,
            <MenuItem key={ClientStatus.Onboarding} value={ClientStatus.Onboarding}>
                Onboarding
            </MenuItem>,
            <MenuItem key={ClientStatus.Suspended} value={ClientStatus.Suspended}>
                Suspended
            </MenuItem>

        ];

    };

    const getNotificationModeOptions = () => {
        if (notificationModes && notificationModes.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const mappedItems = (
                notificationModes.map((mode: MissingAcknowledgementNotificationMode) => (
                    <MenuItem
                        key={mode}
                        value={mode}
                    >
                        {capitalizeWithUnderscoreRemoval(mode, " ")}
                    </MenuItem>
                ))
            );
            items.push(blankItem);
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    const getErpDocumentFormatOptions = () => {
        if (erpDocumentFormats && erpDocumentFormats.length) {
            let items = [];
            const blankItem = <BlankMenuItem key="0" value=""></BlankMenuItem>;
            const mappedItems = (
                erpDocumentFormats.map((format: ErpDocumentFormat) => (
                    <MenuItem
                        key={format}
                        value={format}
                    >
                        {format !== ErpDocumentFormat.Csv ? capitalizeWithUnderscoreRemoval(format, " ") : format}
                    </MenuItem>
                ))
            );
            items.push(blankItem);
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    // only displaying clientAKA list when already have a client
    // since the clientId is needed to save a clientAKA
    const getClientAkaList = () => {
        return (
            <DialogClientAkaList
                parentId={clientId ?? ''}
                clientAkas={clientAKAs}
                isLoading={submitted}
                isClientFormDirty={isFormDirty}
                refreshClientData={refreshClientData}
                preSaveClientData={preSaveClientData}
            />
        );
    };

    const businessAreaProps = {
        'aria-label': 'businessArea',
    };
    const codeProps = {
        'aria-label': 'code',
        'maxLength': 50,
    };
    const currencyCodeProps = {
        'aria-label': 'default currency code',
        'maxLength': 3,
    };
    const emailProps = {
        'aria-label': 'email',
        'maxLength': 255,
    };
    const erpProductProps = {
        'aria-label': 'erpProduct',
    };
    const nameProps = {
        'aria-label': 'name',
        'maxLength': 255,
    };
    const internalClientCodeProps = {
        'aria-label': 'internal client code',
        'maxLength': 25,
    };
    const tenantIdProps = {
        'aria-label': 'tenant Id',
        'maxLength': 36,
    };
    const websiteProps = {
        'aria-label': 'website',
        'maxLength': 255,
    };
    const statusProps = {
        'aria-label': 'clientStatus',
    };
    const sfCountryCodeProps = {
        'aria-label': 'salesforce country code',
        'maxLength': 10,
    };
    const sfCustomerNumberProps = {
        'aria-label': 'salesforce customer number',
        'maxLength': 50,
    };
    const sfEntityCodeProps = {
        'aria-label': 'salesforce entity code',
        'maxLength': 20,
    };
    
    return (

        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            id={clientId}
            entityName="Client"
            maxWidth='md'
            onClose={onClose}
            onSave={submitForm}
            validate={isFormValid}
            onError={onError}
            error={error || fetchError}
        >
            <Grid container spacing={2} item xs={12}>
                <DialogGridWithTopMargin container item columns={{ xs: 1, sm: 2 }} spacing={2} xs={12}>
                    <DialogGridColumn id="detailsColumn" container item xs={12} sm={6}>
                        <Grid item xs={12}>
                            <Stack spacing={2}>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-name"
                                        fullWidth
                                        disabled={submitted}
                                        autoFocus
                                        value={name}
                                        label="Name"
                                        inputProps={nameProps}
                                        onChange={onNameChange}
                                        autoComplete="off"
                                        required
                                        data-cy="dialog-client-name"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-code"
                                        fullWidth
                                        disabled={submitted}
                                        value={code}
                                        label="Code"
                                        inputProps={codeProps}
                                        onChange={onCodeChange}
                                        autoComplete="off"
                                        required
                                        data-cy="dialog-client-code"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-internal-client-code"
                                        fullWidth
                                        disabled={submitted}
                                        value={internalClientCode}
                                        label="Internal Client Code (auto-generated if not provided)"
                                        inputProps={internalClientCodeProps}
                                        onChange={onInternalClientCodeChange}
                                        autoComplete="off"
                                        data-cy="dialog-client-internal-client-code"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <IsaQualifierField
                                        disabled={submitted}
                                        value={clientQ}
                                        onChange={onClientQChange}
                                        required
                                        data-cy="dialog-client-qualifier"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-website"
                                        fullWidth
                                        error={websiteInvalid}
                                        disabled={submitted}
                                        helperText={websiteErrorText}
                                        value={website}
                                        label="Website"
                                        inputProps={websiteProps}
                                        onChange={onWebsiteChange}
                                        autoComplete="off"
                                        data-cy="dialog-client-website"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-email"
                                        fullWidth
                                        error={emailInvalid}
                                        helperText={emailErrorText}
                                        disabled={submitted}
                                        value={email?.email}
                                        label="Email"
                                        inputProps={emailProps}
                                        onChange={onEmailChange}
                                        autoComplete="off"
                                        required
                                        data-cy="dialog-client-email"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-client-businessarea-label">Business Area</InputLabel>
                                        <Select
                                            labelId="dialog-client-businessarea-label"
                                            aria-labelledby="dialog-client-businessarea-label"
                                            value={businessAreaId ?? ''}
                                            onChange={onBusinessAreaChange}
                                            disabled={submitted}
                                            MenuProps={businessAreaProps}
                                            data-cy="dialog-client-businessarea"
                                        >
                                            {getBuisnessAreaOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-client-erpproduct-label" required>ERP Product</InputLabel>
                                        <Select
                                            labelId="dialog-client-erpproduct-label"
                                            aria-labelledby="dialog-client-erpproduct-label"
                                            required
                                            value={erpProductId ?? ''}
                                            onChange={onErpProductChange}
                                            disabled={submitted}
                                            MenuProps={erpProductProps}
                                            data-cy="dialog-client-erpproduct"
                                        >
                                            {getErpProductsOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item >
                                    <TextField
                                        itemID="dialog-client-default-currency-code"
                                        fullWidth
                                        required
                                        disabled={submitted}
                                        value={currencyCode}
                                        label="Default Currency Code"
                                        inputProps={currencyCodeProps}
                                        onChange={onCurrencyCodeChange}
                                        autoComplete="off"
                                        data-cy="dialog-client-default-currency-code"
                                        variant="standard"
                                        onFocus={event => {
                                            // since field is defaulted, highlight value for easy editing
                                            event.target.select();
                                        }}
                                    />
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-client-billing-group-label" required>Billing Group</InputLabel>
                                        <Select
                                            labelId="dialog-client-billing-group-label"
                                            aria-labelledby="dialog-client-billing-group-label"
                                            required
                                            value={billingId ?? ''}
                                            onChange={onBillingGroupChange}
                                            disabled={submitted}
                                            displayEmpty
                                            MenuProps={{
                                                'aria-label': 'billing group',
                                            }}
                                            data-cy="dialog-client-billing-group"
                                        >
                                            {getBillingOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item >
                                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                                        <DatePicker 
                                            label="Billing Start Date"
                                            value={billingStartDate ?? null}
                                            views={['year', 'month', 'day']}
                                            openTo={'day'}
                                            minDate={minValidBillingDate}
                                            disabled={submitted}
                                            onChange={onBillingStartDateChange}
                                            onError={onBillingStartDateError}
                                            slotProps={{
                                                textField: {
                                                    variant: "standard",
                                                    fullWidth: true,
                                                    helperText: billingStartDateErrorText,
                                                    inputProps: {'aria-label': 'billing start date'}
                                                },
                                            }}
                                            data-cy="dialog-client-billing-start-date"
                                        ></DatePicker>
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item >
                                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                                        <TimeField
                                            label="Bulk Notification Time"
                                            variant="standard"
                                            fullWidth
                                            value={notificationTime}
                                            format="hh:mm a"
                                            onChange={onNotificationTimeChange}
                                            data-cy="dialog-client-bulk-notification-time"
                                        ></TimeField>
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item >
                                    <FormControl variant="standard" fullWidth>
                                        <InputLabel id="dialog-client-notification-mode-label">Missing Acknowledgement Notification Mode</InputLabel>
                                        <Select
                                            labelId="dialog-client-notification-mode-label"
                                            aria-labelledby="dialog-client-notification-mode-label"
                                            value={notificationMode ?? ''}
                                            onChange={onNotificationModeChange}
                                            disabled={submitted}
                                            displayEmpty
                                            MenuProps={{
                                                'aria-label': 'missing acknowledgement notification mode',
                                            }}
                                            data-cy="dialog-client-notification-mode"
                                        >
                                            {getNotificationModeOptions()}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item>
                                    <TextField
                                        itemID="dialog-client-salesForceCountryCode"
                                        fullWidth
                                        disabled={submitted}
                                        value={salesforceCountryCode}
                                        label="Salesforce Country Code"
                                        inputProps={sfCountryCodeProps}
                                        onChange={onSfCountryCodeChange}
                                        autoComplete="off"
                                        data-cy="dialog-client-salesForceCountryCode"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        itemID="dialog-client-salesForceEntityCode"
                                        fullWidth
                                        disabled={submitted}
                                        value={salesforceEntityCode}
                                        label="Salesforce Entity Code"
                                        inputProps={sfEntityCodeProps}
                                        onChange={onSfEntityCodeChange}
                                        autoComplete="off"
                                        data-cy="dialog-client-salesForceEntityCode"
                                        variant="standard"
                                    />
                                </Grid>
                            </Stack>
                        </Grid>
                    </DialogGridColumn>
                    <DialogGridColumn id="addressColumn" container item xs={12} sm={6}>
                        <Grid item xs={12}>
                            <AddressFields
                                parentId={clientId}
                                address={address}
                                onChange={onAddressChange}
                                disabled={submitted}
                                data-cy="dialog-client-address-fields"
                            />
                        </Grid>
                        <DialogGridWithTopMargin item xs={12}>
                            <PhoneFields
                                phone={phone}
                                parentId={clientId}
                                disabled={submitted}
                                onChange={onPhoneNumberChange}
                                data-cy="dialog-client-phone"
                            />
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <TextField
                                itemID="dialog-client-tenantID"
                                fullWidth
                                error={tenantIdInvalid}
                                helperText={tenantIdErrorText}
                                disabled={submitted}
                                value={tenantId}
                                placeholder={emptyGuid}
                                label="Tenant ID (GUID)"
                                inputProps={tenantIdProps}
                                onChange={onTenantIdChange}
                                autoComplete="off"
                                data-cy="dialog-client-tenantID"
                                variant="standard"
                            />
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel id="dialog-client-status-label">Client Status</InputLabel>
                                <Select
                                    labelId="dialog-client-status-label"
                                    aria-labelledby="dialog-client-status-label"
                                    value={status}
                                    onChange={onStatusChange}
                                    disabled={submitted}
                                    MenuProps={statusProps}
                                    data-cy="dialog-client-status"
                                >
                                    {getStatusOptions()}
                                </Select>
                            </FormControl>
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel id="dialog-client-error-formatter-label">Error Formatter</InputLabel>
                                <Select
                                    labelId="dialog-client-error-formatter-label"
                                    aria-labelledby="dialog-client-error-formatter-label"
                                    value={errorFormatterId ?? ''}
                                    onChange={onErrorFormatterChange}
                                    disabled={submitted}
                                    MenuProps={{
                                        'aria-label': 'error formatter',
                                    }}
                                    data-cy="dialog-client-error-formatter"
                                >
                                    {getErrorFormatterOptions()}
                                </Select>
                            </FormControl>
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <LocaleField
                                disabled={submitted}
                                value={localeCode ?? ''}
                                onChange={onLocaleChange}
                                data-cy="dialog-client-locale"
                            />
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <FormControl variant="standard" fullWidth>
                                <InputLabel id="dialog-client-erp-document-format-label">ERP Document Format</InputLabel>
                                <Select
                                    labelId="dialog-client-erp-document-format-label"
                                    aria-labelledby="dialog-client-erp-document-format-label"
                                    value={erpDocumentFormat ?? ''}
                                    onChange={onErpDocumentFormatChange}
                                    disabled={submitted}
                                    displayEmpty
                                    MenuProps={{
                                        'aria-label': 'ERP Document Format',
                                    }}
                                    data-cy="dialog-client-erp-document-format"
                                >
                                    {getErpDocumentFormatOptions()}
                                </Select>
                            </FormControl>
                        </DialogGridWithTopMargin>
                        <DialogGridWithTopMargin item xs={12}>
                            <TextField
                                itemID="dialog-client-salesForceCustomerNumber"
                                fullWidth
                                disabled={submitted}
                                value={salesforceCustomerNumber}
                                label="Salesforce Customer Number"
                                inputProps={sfCustomerNumberProps}
                                onChange={onSfCustomerNumberChange}
                                autoComplete="off"
                                data-cy="dialog-client-salesForceCustomerNumber"
                                variant="standard"
                            />
                        </DialogGridWithTopMargin>
                    </DialogGridColumn>
                </DialogGridWithTopMargin>
                {clientInfo?.id &&
                    <Grid item xs={12}>
                        {getClientAkaList()}
                    </Grid>
                }

            </Grid>
        </AddEditDialog>

    );
};

export default ClientDialog;